import { PlusOutlined } from "@ant-design/icons"
import { QueryParamsFilter } from "@finway-group/shared/lib/models"
import { Button, Tag, Tooltip } from "antd"
import { PurchaseOrderTagIcon } from "Components/icons/PurchaseOrderTagIcon"
import { PurchaseOrderSelectionModal } from "Components/modals/PurchaseOrderSelection.modal"
import React from "react"
import { useTranslation } from "react-i18next"
import { useModal } from "Shared/context/modal.context"
import { PurchaseOrderReference } from "Shared/models/purchaseOrderReference"

type PurchaseOrderSelectionButtonProps = {
    selectedPurchaseOrders: Array<PurchaseOrderReference>
    setSelectedPurchaseOrders: (selectedPurchaseOrders: Array<PurchaseOrderReference>) => void
    enabled: boolean
    tooltipTitleKey?: string
    tooltipPlacement: "top" | "left"
    /** Use when adding an expense/DN directly from the PO detail page, to lock the selection to that specific PO */
    isLocked?: boolean
    purchaseOrderFilter: QueryParamsFilter
}

export const PurchaseOrderSelectionButton = ({
    selectedPurchaseOrders,
    setSelectedPurchaseOrders,
    enabled,
    tooltipTitleKey,
    tooltipPlacement,
    isLocked = false,
    purchaseOrderFilter,
}: PurchaseOrderSelectionButtonProps) => {
    const { showModal } = useModal()
    const { t } = useTranslation()
    const handleClick = () => {
        showModal(PurchaseOrderSelectionModal, true, {
            isShowing: true,
            onSelection: setSelectedPurchaseOrders,
            defaultSelectedPurchaseOrders: selectedPurchaseOrders,
            defaultFilter: purchaseOrderFilter,
        })
    }

    const text = selectedPurchaseOrders.length === 0 ? t("action:po_link.select_purchase_orders") : undefined

    const tooltipTitle = enabled ? undefined : t(tooltipTitleKey ?? "label:purchase_order_link_disabled")

    return (
        <div className="flex justify-between mt-12 mr-10">
            {selectedPurchaseOrders?.length > 0 && (
                <div className="flex flex-wrap gap-8">
                    {selectedPurchaseOrders.map((po) => (
                        <Tag
                            key={po._id}
                            closable={!po.alreadyLinked && !isLocked}
                            onClose={() => {
                                const updatedPOs = selectedPurchaseOrders.filter((item) => item._id !== po._id)
                                setSelectedPurchaseOrders(updatedPOs)
                            }}
                            className="mb-4 flex items-center align-middle"
                        >
                            <PurchaseOrderTagIcon className="mr-4" />
                            {po.purchaseOrderNumber}
                        </Tag>
                    ))}
                </div>
            )}
            {!isLocked && (
                <Tooltip title={tooltipTitle} placement={tooltipPlacement}>
                    <Button type="default" onClick={handleClick} className="text-center" disabled={!enabled}>
                        <PlusOutlined />
                        {text && <span className="ml-8">{text}</span>}
                    </Button>
                </Tooltip>
            )}
        </div>
    )
}
